import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { galleryApi } from '../../services/gallery';

export const Navbar = ({ user, logout, onOpenAuth, onUploadClick, onSearch, onClearSearch }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [hidden, setHidden] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  
  // Состояние для модального окна "Предложить фото"
  const [suggestModalOpen, setSuggestModalOpen] = useState(false);
  
  // Состояния поиска
  const [query, setQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTypeIds, setSelectedTypeIds] = useState([]);
  const [availableTypes, setAvailableTypes] = useState([]);
  
  // Маппинг иконок для типов
  const typeIcons = {
    'Фотография': '📷', 'фотография': '📷',
    'Фотоальбом': '📔', 'фотоальбом': '📔',
    'Стенгазета': '📰', 'стенгазета': '📰',
    'default': '📄'
  };
  const getTypeIcon = (title) => typeIcons[title] || typeIcons['default'];

  // 1. Загрузка тегов и типов при открытии поиска
  useEffect(() => {
    if (searchOpen) {
      if (availableTags.length === 0) {
        galleryApi.getTags()
          .then(data => setAvailableTags(Array.isArray(data) ? data : (data.tags || [])))
          .catch(() => setAvailableTags([]));
      }
      if (availableTypes.length === 0) {
        galleryApi.getContentTypes()
          .then(data => setAvailableTypes(Array.isArray(data) ? data : (data.types || [])))
          .catch(() => setAvailableTypes([]));
      }
    }
  }, [searchOpen]);

  // 2. Скрытие навбара при скролле вниз
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setHidden(currentScrollY > 100 && currentScrollY > lastScrollY);
      setLastScrollY(currentScrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // 3. Закрытие поиска по Esc
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        if (searchOpen) setSearchOpen(false);
        if (suggestModalOpen) setSuggestModalOpen(false);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [searchOpen, suggestModalOpen]);

  // Обработчики поиска
  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    if (!onSearch) return;
    
    // Получаем названия всех выбранных типов
    const typeNames = selectedTypeIds.length > 0
      ? availableTypes
          .filter(t => selectedTypeIds.includes(t.pk_type))
          .map(t => t.title)
      : [];
    
    console.log('🔍 Отправляю поиск:', {
      query: query.trim(),
      dateFrom,
      dateTo,
      tags: selectedTagIds,
      types: typeNames  // Массив названий типов
    });
    
    onSearch({
      query: query.trim(),
      dateFrom,
      dateTo,
      tags: selectedTagIds,
      types: typeNames  // Передаём массив
    });
    setSearchOpen(false);
  };

  const handleClearSearch = () => {
    setQuery('');
    setDateFrom('');
    setDateTo('');
    setSelectedTagIds([]);
    setSelectedTypeIds([]);
    if (onClearSearch) onClearSearch();
    setSearchOpen(false);
  };

  const toggleTag = (id) => setSelectedTagIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  const toggleType = (id) => setSelectedTypeIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);

  // Не рендерим навбар на внутренних страницах
  if (location.pathname.startsWith('/album/') || location.pathname.startsWith('/photo/')) {
    return null;
  }

  return (
    <>
      {/* === ФОН-ЗАТЕМНЕНИЕ ДЛЯ ПОИСКА === */}
      {searchOpen && (
        <div 
          className="search-backdrop"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(10, 10, 8, 0.45)',
            zIndex: 9999,
            animation: 'fadeIn 0.3s ease'
          }}
          onClick={() => setSearchOpen(false)}
        />
      )}

      {/* === МОДАЛЬНОЕ ОКНО "ПРЕДЛОЖИТЬ ФОТО" === */}
      {suggestModalOpen && (
        <>
          <div 
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(5, 5, 3, 0.92)',
              zIndex: 100000,
              animation: 'fadeIn 0.2s ease'
            }}
            onClick={() => setSuggestModalOpen(false)}
          />
          <div 
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'linear-gradient(160deg, #1c1c16 0%, #12120e 100%)',
              border: '1.5px solid rgba(201, 169, 110, 0.6)',
              borderRadius: '16px',
              padding: '35px 40px',
              maxWidth: '520px',
              width: '90vw',
              zIndex: 100001,
              boxShadow: '0 25px 80px rgba(0, 0, 0, 0.7)',
              textAlign: 'center',
              animation: 'modalSlideIn 0.3s ease'
            }}
            onClick={e => e.stopPropagation()}
          >
            <button 
              onClick={() => setSuggestModalOpen(false)}
              style={{
                position: 'absolute',
                top: 15,
                right: 20,
                background: 'transparent',
                border: 'none',
                color: 'var(--text-muted)',
                fontSize: '1.8rem',
                cursor: 'pointer',
                lineHeight: 1,
                padding: 5
              }}
            >
              ×
            </button>
            
            <div style={{ fontSize: '3rem', marginBottom: 20 }}>📬</div>
            
            <h3 style={{ 
              fontFamily: 'Cormorant Garamond, serif', 
              fontSize: '1.6rem', 
              color: 'var(--accent)', 
              marginBottom: 15,
              margin: '0 0 15px 0'
            }}>
              Предложить фотографии
            </h3>
            
            <p style={{ 
              color: 'var(--text-light)', 
              fontSize: '1rem', 
              lineHeight: 1.6, 
              marginBottom: 25,
              margin: '0 0 25px 0'
            }}>
              Если у вас есть архивные фотографии, посвященные нашему МАУ, 
              присылайте их к нам на электронную почту:
            </p>
            
            <a 
              href="mailto:archive@mau-kirovsk.ru"
              style={{
                display: 'inline-block',
                background: 'rgba(201, 169, 110, 0.15)',
                border: '1px solid var(--accent)',
                color: 'var(--accent)',
                padding: '12px 28px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontFamily: 'Special Elite, monospace',
                fontSize: '1.05rem',
                letterSpacing: '1px',
                transition: 'all 0.2s',
                marginBottom: 20
              }}
              onMouseEnter={e => {
                e.target.style.background = 'var(--accent)';
                e.target.style.color = 'var(--bg-dark)';
              }}
              onMouseLeave={e => {
                e.target.style.background = 'rgba(201, 169, 110, 0.15)';
                e.target.style.color = 'var(--accent)';
              }}
            >
              archive@mau-kirovsk.ru
            </a>
            
            <p style={{ 
              color: 'var(--text-muted)', 
              fontSize: '0.85rem', 
              fontStyle: 'italic',
              margin: 0
            }}>
              Мы обязательно рассмотрим ваши материалы и свяжемся с вами
            </p>
          </div>
        </>
      )}

      {/* === НАВБАР === */}
      <nav className={hidden ? 'nav-hidden' : ''}>
        <a href="#hero" className="nav-logo" onClick={() => window.scrollTo(0, 0)}>ФОТОАРХИВ</a>
        
        <ul className="nav-links">
          <li><a href="#gallery">Галерея</a></li>
          <li><a href="#about">О проекте</a></li>
        </ul>
        
        <div className="nav-right">
          <button className="search-toggle-btn" onClick={() => setSearchOpen(!searchOpen)} title="Поиск">🔍</button>
          
          {user ? (
            <>
              <span className="nav-user-badge">{user.email}</span>
              <button className="nav-auth-btn" onClick={logout}>Выйти</button>
              {user.is_admin && (
                <button className="nav-auth-btn" onClick={() => navigate('/admin')}>⚙️ Админка</button>
              )}
            </>
          ) : (
            <>
              <button className="nav-auth-btn" onClick={() => onOpenAuth?.('login')}>Войти</button>
              <button className="nav-auth-btn primary" onClick={() => onOpenAuth?.('register')}>Регистрация</button>
            </>
          )}
          
          {user?.is_admin ? (
            <button className="nav-upload-btn" onClick={onUploadClick}>
              <span>+</span> Добавить
            </button>
          ) : (
            <button 
              className="nav-upload-btn" 
              onClick={() => setSuggestModalOpen(true)}
              style={{
                background: 'rgba(201, 169, 110, 0.1)',
                border: '1px dashed rgba(201, 169, 110, 0.5)'
              }}
              title="Предложить свои фото"
            >
              <span>✉️</span> Предложить фото
            </button>
          )}
        </div>
      </nav>

      {/* === ПАНЕЛЬ ПОИСКА === */}
      {searchOpen && (
        <div 
          className="search-panel" 
          onClick={(e) => e.stopPropagation()}
          style={{ zIndex: 10000 }}
        >
          <div className="search-panel-header">
            <h3>Поиск по архиву</h3>
            <button className="search-close" onClick={() => setSearchOpen(false)}>×</button>
          </div>
          
          <div className="search-section">
            <label className="search-label">Текст</label>
            <input 
              type="text" 
              className="search-input" 
              placeholder="Название, описание, автор..." 
              value={query} 
              onChange={(e) => setQuery(e.target.value)} 
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearch(e);
                }
              }}
            />
          </div>
          
          <div className="search-section">
            <label className="search-label">Период</label>
            <div className="date-range">
              <input type="number" className="search-input date-input" placeholder="От" min="1800" max="2100" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              <span className="date-separator">—</span>
              <input type="number" className="search-input date-input" placeholder="До" min="1800" max="2100" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
          </div>
          
          <div className="search-section">
            <label className="search-label">Тип контента</label>
            <div className="type-filter">
              {availableTypes.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '10px' }}>Загрузка типов...</div>
              ) : (
                availableTypes.map(type => (
                  <button
                    key={type.pk_type}
                    type="button"
                    className={`type-filter-chip ${selectedTypeIds.includes(type.pk_type) ? 'selected' : ''}`}
                    onClick={() => toggleType(type.pk_type)}
                  >
                    <span style={{ marginRight: '6px' }}>{getTypeIcon(type.title)}</span>
                    {type.title}
                  </button>
                ))
              )}
            </div>
            {selectedTypeIds.length > 0 && (
              <small style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '8px', display: 'block' }}>
                Выбрано типов: {selectedTypeIds.length}
              </small>
            )}
          </div>
          
          <div className="search-section">
            <label className="search-label">Теги</label>
            <div className="tags-filter">
              {availableTags.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '10px' }}>Загрузка тегов...</div>
              ) : (
                availableTags.map(tag => (
                  <button
                    key={tag.pk_tag || tag.id}
                    type="button"
                    className={`tag-filter-chip ${selectedTagIds.includes(tag.pk_tag || tag.id) ? 'selected' : ''}`}
                    onClick={() => toggleTag(tag.pk_tag || tag.id)}
                  >
                    {tag.title || tag.name}
                  </button>
                ))
              )}
            </div>
            {selectedTagIds.length > 0 && (
              <small style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '8px', display: 'block' }}>
                Выбрано тегов: {selectedTagIds.length}
              </small>
            )}
          </div>
          
          <div className="search-actions">
            <button className="search-btn" onClick={handleSearch}>Найти</button>
            <button className="clear-btn" onClick={handleClearSearch}>Сбросить</button>
          </div>
        </div>
      )}
    </>
  );
};